import {GetServerSideProps} from "next";
import Page from "@components/page";
import StandardLayout from "@components/layout/standard-layout";
import {getPage, getHomePageId, getPageChildTree} from "@lib/confluence/confluence-api";
import {
    BodyExpandable, ChildExpandable,
    ConfluencePage, Space
} from "@lib/confluence/confluence-model";
import {ConfluenceArticle} from "@components/confluence/confluence-article";
import {TableOfContents} from "@components/table-of-contents";
import {NavigationData, NavigationFolderData, NavigationInternalLinkData} from "@lib/nav/link";
import SideNav from "@components/side-nav/tree-nav/side-nav";
import React from "react";

type Props = {
    page: ConfluencePage & BodyExpandable
    spaceKey: string
} & NavigationData

type Params = {
    'page-id': string
}


const ConfluencePageComponent = ({page, navFolder, currentSlug, spaceKey}: Props) => {
    const toc = <TableOfContents dependencies={[page.id]}/>
    const leftNav = <SideNav
        navFolder={navFolder}
            currentSlug={currentSlug}
    />
    return (
        <Page>
            <StandardLayout
                leftDrawerContent={leftNav}
                rightDrawerContent={toc}
                title={spaceKey}
            >
                <ConfluenceArticle article={page}/>
            </StandardLayout>
        </Page>
    )
}

export default ConfluencePageComponent

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({params}) => {
    if (!params) {
        return {
            notFound: true,
        }
    }
    const pageId = params["page-id"]
    try {
        const page = await getPage(pageId)
        const spaceKey = getHomePageId(page)
        const spaceWithChildren = await getPageChildTree(spaceKey)
        const currentSlug = `wiki/${pageId}`
        const navFolder = pageTreeToNavTree(spaceWithChildren, page.space)
        return {
            props: {
                page,
                currentSlug,
                navFolder,
                spaceKey: page.space.key,
            },
        }
    } catch (e) {
        return {
            notFound: true,
        }
    }
}

const pageTreeToNavTree = (homePage: ConfluencePage & ChildExpandable, space: Space): NavigationFolderData => ({
    type: 'Folder',
    label: space.name,
    uid: homePage.id,
    fullSlug: `/wiki/${homePage.id}`,
    children: homePage.children?.page?.results?.map(parseChildren) ?? [],
})

const parseChildren = (page: ConfluencePage & ChildExpandable): NavigationFolderData | NavigationInternalLinkData => {
    const results = page.children?.page?.results
    const uid = page.id
    const fullSlug = `/wiki/${page.id}`
    const label = page.title
    if(results && results.length > 0){
        // Has children, return folder
        return ({
            type: 'Folder',
            uid,
            fullSlug,
            label,
            children: results.map(parseChildren),
        })
    } else {
        // Leaf node, return link
        return ({
            type: 'InternalLink',
            uid,
            fullSlug,
            label,
        })
    }
}